import { memo, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

const DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

// Smooth deceleration. Because the strip slides past the in-between digits on its
// way to the target, a larger digit change visibly "spins" through them.
const ROLL_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

/**
 * One odometer digit: a vertical 0–9 strip clipped to a single row that slides to
 * the current digit. A pure CSS transition, so it rolls only when the digit
 * changes — never on first paint — and snaps under prefers-reduced-motion (the
 * global guard in index.css overrides the transition to ~0ms).
 *
 * `delayMs` staggers columns so a multi-digit change ripples ("shuffle") instead
 * of every column moving in lockstep. Memoized on its (primitive) props.
 */
const RollingDigit = memo(function RollingDigit({
  digit,
  durationMs,
  delayMs,
}: {
  digit: number;
  durationMs: number;
  delayMs: number;
}) {
  return (
    <span className="inline-block h-[1em] overflow-hidden">
      <span
        className="flex flex-col"
        style={{
          transform: `translateY(-${digit}em)`,
          transition: `transform ${durationMs}ms ${ROLL_EASE} ${delayMs}ms`,
        }}
      >
        {DIGITS.map((d) => (
          <span key={d} className="flex h-[1em] items-center justify-center leading-none">
            {d}
          </span>
        ))}
      </span>
    </span>
  );
});

export interface RollingNumberProps {
  /** The number to display. Digit positions that change roll like an odometer. */
  value: number;
  /** Roll duration in milliseconds. Default 600. */
  durationMs?: number;
  /** Per-column stagger (ms) from the units up, so a multi-digit change ripples
   *  into a "shuffle" rather than moving in lockstep. 0 disables it. Default 45. */
  staggerMs?: number;
  /** Locale thousands grouping (e.g. "7,840"). Set false for no separators. */
  grouping?: boolean;
  className?: string;
}

/**
 * Odometer-style number. Each digit is a vertical strip that slides to its value,
 * so only the digits that actually change roll — and only on change, never on
 * first paint. Non-digit characters (commas, minus) render static. Pure React +
 * CSS, no dependencies.
 *
 * Memoized: re-renders only when its (primitive) props change, so it's safe to
 * drop into long lists — an unchanged value costs nothing even if the parent
 * re-renders.
 *
 * @example
 * <RollingNumber value={stats.completed} />
 * <RollingNumber value={score} durationMs={800} grouping={false} className="text-3xl" />
 */
export const RollingNumber = memo(function RollingNumber({
  value,
  durationMs = 600,
  staggerMs = 45,
  grouping = true,
  className,
}: RollingNumberProps) {
  const rounded = Math.round(value);
  // Non-finite (NaN / ±Infinity) can't be rolled — fall back to plain static text.
  if (!Number.isFinite(rounded)) {
    return <span className={cn('tabular-nums', className)}>{String(value)}</span>;
  }
  const text = grouping ? rounded.toLocaleString() : String(rounded);

  // Build slots left-to-right, but key each digit by its place value *from the
  // right* (units = "d-0", tens = "d-1", …) and each grouping comma by the place
  // it precedes. So when the number widens (9,999 → 10,000) every existing digit
  // and comma keeps a stable key — only the new leading digit (and its comma)
  // mount. Digits no longer shuffle positions or roll to unrelated values; it
  // reads as a proper carry. Still stateless / pure-CSS.
  const digits = String(Math.abs(rounded)).split('').map(Number); // leftmost first
  const len = digits.length;
  const slots: ReactNode[] = [];
  if (rounded < 0) {
    slots.push(
      <span key="minus" className="inline-block">
        -
      </span>
    );
  }
  digits.forEach((d, j) => {
    const place = len - 1 - j;
    if (grouping && j !== 0 && (len - j) % 3 === 0) {
      slots.push(
        <span key={`sep-${place}`} className="inline-block">
          ,
        </span>
      );
    }
    slots.push(
      <RollingDigit
        key={`d-${place}`}
        digit={d}
        durationMs={durationMs}
        delayMs={place * staggerMs}
      />
    );
  });

  return (
    <span className={cn('inline-flex items-end leading-none tabular-nums', className)}>
      {/* Screen readers get the plain value once; the rolling strips (which each
          contain every 0–9) are hidden so they aren't announced as "0 1 2 3 …". */}
      <span className="sr-only">{text}</span>
      <span aria-hidden="true" className="inline-flex items-end">
        {slots}
      </span>
    </span>
  );
});
